import { useEffect, useMemo, useState } from "react";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { ShieldAlert, FileType2, FileSpreadsheet, Search, ChevronDown, ChevronRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

export default function TrainerResults() {
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => { api.get("/results").then(r => setResults(r.data)); }, []);

  // Group results by assessment_id
  const groups = useMemo(() => {
    const byId = new Map();
    for (const r of results) {
      const key = r.assessment_id;
      if (!byId.has(key)) {
        byId.set(key, {
          assessment_id: r.assessment_id,
          assessment_title: r.assessment_title,
          rows: [],
        });
      }
      byId.get(key).rows.push(r);
    }
    // Compute per-assessment stats
    return Array.from(byId.values()).map((g) => {
      const passed = g.rows.filter(r => r.status === "passed").length;
      const failed = g.rows.filter(r => r.status === "failed").length;
      const avg = g.rows.length
        ? Math.round((g.rows.reduce((a, b) => a + (b.percentage || 0), 0) / g.rows.length) * 10) / 10
        : 0;
      const latest = g.rows.reduce((acc, r) => (
        !acc || r.submitted_at > acc ? r.submitted_at : acc
      ), null);
      return { ...g, passed, failed, avg, latest, attempts: g.rows.length };
    }).sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
  }, [results]);

  const q = filter.toLowerCase();
  const filtered = q
    ? groups.map((g) => ({
        ...g,
        rows: g.rows.filter((r) =>
          (r.student_name || "").toLowerCase().includes(q) ||
          (r.assessment_title || "").toLowerCase().includes(q)
        ),
      })).filter((g) => g.rows.length > 0 || g.assessment_title.toLowerCase().includes(q))
    : groups;

  const download = (assessment_id, title, format) => {
    const url = `${API_BASE}/reports/results/download?assessment_id=${encodeURIComponent(assessment_id)}&format=${format}`;
    window.location.href = url;
    toast.success(`${format.toUpperCase()} download started for “${title}”`);
  };

  return (
    <div>
      <PageHeader
        title="Results"
        subtitle="Assessment-wise employee submissions. Download per-assessment reports as PDF or Excel."
      />

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="results-filter"
          placeholder="Search by employee or assessment…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No results" hint="Employees need to submit assessments first." />
      ) : (
        <div className="space-y-4">
          {filtered.map((g) => {
            const isOpen = expanded[g.assessment_id] !== false;
            return (
              <div key={g.assessment_id}
                data-testid={`results-group-${g.assessment_id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <button
                      type="button"
                      onClick={() => setExpanded({ ...expanded, [g.assessment_id]: !isOpen })}
                      data-testid={`results-toggle-${g.assessment_id}`}
                      className="mt-0.5 p-1 text-slate-500 hover:bg-slate-100 rounded-lg">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center flex-shrink-0">
                      <ClipboardList className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-slate-900 truncate">
                        {g.assessment_title}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-slate-500">
                        <span><b className="text-slate-800">{g.attempts}</b> attempts</span>
                        <span>·</span>
                        <span className="text-emerald-700"><b>{g.passed}</b> passed</span>
                        <span>·</span>
                        <span className="text-red-700"><b>{g.failed}</b> failed</span>
                        <span>·</span>
                        <span>avg <b className="text-slate-800">{g.avg}%</b></span>
                      </div>
                    </div>
                  </div>

                  {/* Per-assessment download buttons */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`download-pdf-${g.assessment_id}`}
                      onClick={() => download(g.assessment_id, g.assessment_title, "pdf")}
                      className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-red-400 hover:bg-red-50 text-slate-700 rounded-lg px-3 py-1.5 text-sm font-medium">
                      <FileType2 className="w-4 h-4 text-red-600" /> PDF
                    </button>
                    <button
                      data-testid={`download-xlsx-${g.assessment_id}`}
                      onClick={() => download(g.assessment_id, g.assessment_title, "xlsx")}
                      className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 rounded-lg px-3 py-1.5 text-sm font-medium">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel
                    </button>
                  </div>
                </div>

                {isOpen && g.rows.length > 0 && (
                  <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-left">
                        <tr>
                          <th className="px-5 py-2.5 font-medium">Employee</th>
                          <th className="px-5 py-2.5 font-medium">Score</th>
                          <th className="px-5 py-2.5 font-medium">%</th>
                          <th className="px-5 py-2.5 font-medium">Time</th>
                          <th className="px-5 py-2.5 font-medium">Integrity</th>
                          <th className="px-5 py-2.5 font-medium">Submitted</th>
                          <th className="px-5 py-2.5 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => {
                          const vc = r.violation_count || 0;
                          return (
                            <tr key={r.id} className="border-t border-slate-100">
                              <td className="px-5 py-2.5 font-medium text-slate-900">{r.student_name}</td>
                              <td className="px-5 py-2.5 text-slate-700">{r.score}/{r.total}</td>
                              <td className="px-5 py-2.5 font-medium">{r.percentage}%</td>
                              <td className="px-5 py-2.5 text-slate-500">
                                {Math.floor((r.time_taken || 0) / 60)}m {(r.time_taken || 0) % 60}s
                              </td>
                              <td className="px-5 py-2.5">
                                {vc > 0 || r.auto_submitted ? (
                                  <span data-testid={`integrity-${r.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                                    <ShieldAlert className="w-3.5 h-3.5" />
                                    {vc} {vc === 1 ? "flag" : "flags"}{r.auto_submitted ? " · auto" : ""}
                                  </span>
                                ) : (
                                  <span className="text-xs text-emerald-600">Clean</span>
                                )}
                              </td>
                              <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">
                                {r.submitted_at?.slice(0, 16).replace("T", " ")}
                              </td>
                              <td className="px-5 py-2.5">
                                <Pill tone={r.status === "passed" ? "green" : "red"}>{r.status}</Pill>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {isOpen && g.rows.length === 0 && (
                  <div className="border-t border-slate-100 px-5 py-6 text-center text-sm text-slate-500">
                    No submissions match your search.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
